import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Search } from 'lucide-react';

import { auth } from '@dashai/lib/firebase';

const PaletteShell = lazy(() => import('./GlobalCommandPaletteShell'));

interface AuthSliceLite {
  isAuthenticated?: boolean;
  id_client?: string | null;
  user?: { uid?: string };
}

function isTypingTarget(el: Element | null): boolean {
  if (!el) return false;
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

function isPaletteInput(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.dataset.globalPaletteInput === 'true';
}

export function GlobalCommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = useSelector(
    (s: { auth?: AuthSliceLite }) => Boolean(s.auth?.isAuthenticated),
  );
  const userId = useSelector(
    (s: { auth?: AuthSliceLite }) => s.auth?.user?.uid ?? auth.currentUser?.uid ?? '',
  );
  const workspaceId = useSelector(
    (s: { auth?: AuthSliceLite }) => s.auth?.id_client ?? '',
  );

  const handleClose = useCallback(() => setOpen(false), []);

  const handleAskAI = useCallback(
    (query: string) => {
      const trimmed = query.trim();
      const url = trimmed
        ? `/ai?q=${encodeURIComponent(trimmed)}&autosend=1`
        : '/ai';
      setOpen(false);
      navigate(url);
    },
    [navigate],
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    function onKeyDown(e: KeyboardEvent) {
      const isMod = e.metaKey || e.ctrlKey;
      if (!isMod || e.key.toLowerCase() !== 'k') return;
      // Allow re-trigger inside the palette input itself; otherwise skip when
      // the user is typing into any other text surface.
      const active = document.activeElement;
      if (isTypingTarget(active) && !isPaletteInput(active)) return;
      e.preventDefault();
      setOpen((prev) => !prev);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isAuthenticated]);

  if (!open) return null;

  return (
    <Suspense fallback={null}>
      <PaletteShell
        open={open}
        onClose={handleClose}
        onAskAI={handleAskAI}
        userId={userId}
        workspaceId={workspaceId || ''}
        searchIcon={Search}
      />
    </Suspense>
  );
}

export default GlobalCommandPalette;
